import { useEffect, useState } from 'react';
import { Alert, Button, Modal, Radio } from '@omnitok/ui';
import { LoadingSpinner } from '@/shared/components';
import { localizationService } from '../services/localization.service';
import type { LocalizationRunItem } from '../types/localization.types';
import JsonViewer from './JsonViewer';

interface LocalizationModelSelectionDialogProps {
  runId: string;
  item: LocalizationRunItem | null;
  open: boolean;
  isSelecting: boolean;
  onClose: () => void;
  onSelect: (sku: string, modelId: string) => Promise<void>;
}

export default function LocalizationModelSelectionDialog({
  runId,
  item,
  open,
  isSelecting,
  onClose,
  onSelect,
}: LocalizationModelSelectionDialogProps) {
  const [documents, setDocuments] = useState<Record<string, Record<string, unknown>>>({});
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !item?.candidates?.length) return;
    let active = true;
    setLoading(true);
    setError(null);
    setSelectedId('');
    setDocuments({});
    void Promise.all(item.candidates.map(async ({ modelId }) => ({
      modelId,
      document: await localizationService.getModelCandidate(runId, item.sku, modelId),
    }))).then((results) => {
      if (!active) return;
      setDocuments(Object.fromEntries(results.map(({ modelId, document }) => [modelId, document])));
    }).catch((candidateError: unknown) => {
      if (active) setError(candidateError instanceof Error
        ? candidateError.message
        : 'No fue posible cargar los modelos candidatos.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [item, open, runId]);

  const confirm = async () => {
    if (!item || !selectedId) return;
    setError(null);
    try {
      await onSelect(item.sku, selectedId);
      onClose();
    } catch (selectionError) {
      setError(selectionError instanceof Error
        ? selectionError.message
        : 'No fue posible seleccionar el modelo.');
    }
  };

  return (
    <Modal
      className="localization-modal localization-modal--wide"
      open={open}
      onClose={isSelecting ? () => undefined : onClose}
      closeOnOverlay={!isSelecting}
      closeOnEscape={!isSelecting}
      showCloseButton={!isSelecting}
      title={`Seleccionar modelo para ${item?.sku ?? ''}`}
      size="full"
      footer={(
        <>
          <Button variant="neutral" ghost onClick={onClose} disabled={isSelecting}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={!selectedId || loading || isSelecting}
            loading={isSelecting}
            onClick={() => { void confirm(); }}
          >
            {isSelecting ? 'Seleccionando…' : 'Localizar modelo seleccionado'}
          </Button>
        </>
      )}
    >
      <div className="localization-dialog-content">
        <Alert variant="warning">
          Este SKU corresponde a varios documentos. Compara sus JSON y selecciona el modelId que deseas localizar.
        </Alert>
        {error && <Alert variant="error">{error}</Alert>}
        {loading ? (
          <LoadingSpinner message="Cargando modelos candidatos…" />
        ) : (
          <div className="localization-model-grid">
            {(item?.candidates ?? []).map(({ modelId }) => (
              <section
                className={`localization-model-card${selectedId === modelId ? ' localization-model-card--selected' : ''}`}
                key={modelId}
              >
                <Radio
                  name="localization-model"
                  value={modelId}
                  checked={selectedId === modelId}
                  onChange={() => setSelectedId(modelId)}
                  label={modelId}
                />
                <JsonViewer
                  value={documents[modelId] ?? {}}
                  fileName={`${item?.sku}.${modelId}.json`}
                />
              </section>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
